<script setup>
import { computed, inject } from 'vue'
import LazyImage from '@/components/common/LazyImage/index.vue'
import { isTarget_Blank } from '@/utils/globalData.js'
import { getCurrencyNum, getCurrencySymbol } from '@/utils/goods'
import { trackLowCodeEvent } from '@/utils/lowCodeTracking.js'
import { mallAnalyticsLogEvent } from '@/utils/mallEventLog.js'
import { buildGoodsClickTrackingParams } from './goodsClickTracking.js'
// 接收props
const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  rank: { type: Number, default: 0 },
  source: { type: String, default: 'system' },
  trackingConfig: { type: Object, default: undefined },
  trackingElement: { type: Object, default: () => ({}) },
  elementIndex: {
    type: [Array, String, Number],
    default: 'mall_goods_card_item',
  },
})

const getConfigJson = inject('getConfigJson', () => ({}))

const itemLink = computed(() => {
  return props.item?.link || `/product?detailId=${props.item?.id || ''}`
})

const normalizedPlatform = computed(() => {
  return String(props.item?.platform || props.item?.iconType || '').trim().toLowerCase()
})

const platformLogo = computed(() => {
  return normalizedPlatform.value === 'taobao' || normalizedPlatform.value === 'tb'
    ? '/imgs/icontb.png'
    : normalizedPlatform.value === '1688'
      ? '/imgs/icon1688.png'
      : ''
})
const normalizedFinalPrice = computed(() => {
  const value = Number(props.item?.finalPrice)
  if (!Number.isFinite(value))
    return Number.NaN

  return value
})

const normalizedOriginalPrice = computed(() => {
  const candidates = [
    props.item?.originalPrice,
    props.item?.originPrice,
    props.item?.linePrice,
    props.item?.reservePrice,
    props.item?.zkFinalPrice,
    props.item?.price,
  ]

  for (const candidate of candidates) {
    const value = Number(candidate)
    if (!Number.isFinite(value))
      continue

    return value
  }

  return Number.NaN
})

function trimPriceText(raw) {
  const text = String(raw)

  if (!text.includes('.'))
    return text

  return text
    .replace(/0+$/, '')
    .replace(/\.$/, '')
}

const formattedDisplayPrice = computed(() => {
  const value = normalizedFinalPrice.value
  if (!Number.isFinite(value))
    return ''

  return trimPriceText(getCurrencyNum(value))
})

const formattedOriginalPrice = computed(() => {
  const value = normalizedOriginalPrice.value
  if (!Number.isFinite(value))
    return ''

  return trimPriceText(value)
})

const rawSalesValue = computed(() => {
  return props.item?.salesNum
    ?? props.item?.saleQuantity
    ?? props.item?.sellCount
    ?? props.item?.volume
    ?? props.item?.soldCount
    ?? props.item?.sales
    ?? props.item?.monthSold
})

const showOriginalPrice = computed(() => {
  const originalPrice = normalizedOriginalPrice.value
  const finalPrice = normalizedFinalPrice.value

  return Number.isFinite(originalPrice)
    && Number.isFinite(finalPrice)
    && originalPrice > finalPrice
})

const salesText = computed(() => {
  const raw = rawSalesValue.value

  if (raw == null || raw === '')
    return ''

  const text = String(raw).trim()
  const normalizedText = text.toLowerCase()
  const invalidSalesTexts = new Set([
    '-',
    '--',
    '—',
    '——',
    '— —',
    'null',
    'undefined',
    'nan',
    'n/a',
    '暂无',
    '暂无数据',
    '无',
  ])

  if (invalidSalesTexts.has(normalizedText))
    return ''

  const digitsOnlyText = text.replace(/[^\d.]/g, '')

  if (digitsOnlyText && Number(digitsOnlyText) === 0 && !/[1-9]/.test(text))
    return ''

  return /\d/.test(text) ? text : ''
})

const showSalesRow = computed(() => {
  return !!salesText.value
})

const showHotBadge = computed(() => {
  const hotFields = [
    props.item?.isHot,
    props.item?.hot,
    props.item?.hotFlag,
    props.item?.showHot,
    props.item?.badgeType === 'hot',
    props.item?.tag === 'hot',
  ]

  return hotFields.some(Boolean)
})

function makeOpenImgToSearch(url, platform) {
  if (!url)
    return ''
  const platformQuery
    = String(platform || '').trim() === 'taobao' ? '&platform=taobao' : ''
  return `/product/search?searchType=img&imgSrc=${encodeURIComponent(url)}${platformQuery}`
}


const router = useRouter()
const route = useRoute()
function onDetailClick() {
  const page = typeof getConfigJson === 'function' ? getConfigJson() : {}
  const eventParams = buildGoodsClickTrackingParams(
    props.item,
    props.rank,
    props.source,
  )
  trackLowCodeEvent(props.trackingConfig, {
    trigger: 'click',
    element: props.trackingElement,
    elementType: 'mall_goods_card_item',
    elementIndex: props.elementIndex,
    page,
    binding: {
      ...(page?.bindData || {}),
      goodsData: props.item,
      goodsIndex: Math.max(0, props.rank - 1),
    },
    runtime: {
      goodsIndex: Math.max(0, props.rank - 1),
      platformType: props.item?.platform || props.item?.iconType,
    },
    eventParams,
  })

  router.push(itemLink.value)
}
function onSearchClick() {
  router.push(makeOpenImgToSearch(props.item.imageUrl || props.item.pic, props.item.platform))
}
</script>

<template>
  <div class="goods-item-wrap">
    <div
      v-if="showHotBadge"
      class="goods-hot-badge"
    >
      <span>HOT</span>
    </div>
    <div class="goods-pic-wrap">
      <div class="block" @click="onDetailClick">
        <LazyImage class="goods-pic" :src="item.pic ? item.pic : ''" />
      </div>
      <div
        @click="onSearchClick"
        class="goods-like-link"
        style="display: none;"
      >{{$t('actions.searchLikeGoods')}}</div>
    </div>
    <div class="goods-link block" @click="onDetailClick">
      <div class="goods-info-wrap">
        <div :title="item.title" class="goods-title">
          <img
            v-if="platformLogo"
            class="goods-type-icon"
            :src="platformLogo"
            :alt="item.iconType"
            referrerpolicy="no-referrer"
          >
          <span v-else class="goods-type">{{ item.iconType }}</span>
          <span class="goods-title-text">{{ item.title }}</span>
        </div>
      </div>
      <div class="goods-price-wrap">
        <div class="goods-price">{{ formattedDisplayPrice }} {{ getCurrencySymbol('¥') }}</div>
        <div v-if="showOriginalPrice" class="goods-origin-price">          CN ¥ {{ formattedOriginalPrice }}
        </div>
      </div>
      <div v-if="showSalesRow" class="goods-sales-row">
        <span class="goods-sales-label">{{ $t('texts.saleAmount') }}:</span>
        <span class="goods-sales-value">{{ salesText }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.goods-item-wrap {
  position: relative;
  border-radius: 12px;
  width: 224px;
  margin-right: 20px;
  padding: 12px;
  background-color: #fff;
  margin-bottom: 20px;
  font-size: 14px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(26, 24, 23, 0.1), 0 1px 2px rgba(26, 24, 23, 0.06);
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(26, 24, 23, 0.12);

    .goods-like-link {
      display: block !important;
    }
  }

  .goods-hot-badge {
    position: absolute;
    top: 0;
    left: 12px;
    width: 40px;
    height: 38px;
    z-index: 2;
    background: linear-gradient(180deg, #ff5d4f 0%, #ff2e00 100%);
    clip-path: polygon(0 0, 100% 0, 100% 82%, 50% 100%, 0 82%);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 5px;
    box-sizing: border-box;

    span {
      font-size: 16px;
      line-height: 24px;
      font-weight: 700;
      color: #fff;
      text-transform: uppercase;
      text-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.16);
    }
  }

  .goods-link {
    color: inherit;
    text-decoration: none;
  }

  .goods-pic-wrap {
    position: relative;
    width: 200px;
    height: 200px;
    border-radius: 8px;
    overflow: hidden;
    background: #e5e5e5;
  }

  .goods-like-link {
    display: none;
    position: absolute;
    left: 0;
    bottom: 0;
    height: 32px;
    width: 200px;
    background: #e0787e;
    cursor: pointer;
    color: #fff;
    text-align: center;
    line-height: 32px;
    font-size: 12px;
    text-decoration: none;
    z-index: 1;
  }

  .goods-pic {
    width: 200px;
    height: 200px;
    cursor: pointer;
    display: block;
    object-fit: cover;
  }

  .goods-info-wrap {
    margin-top: 8px;
    min-height: 36px;

    .goods-type {
      padding: 0 4px;
      min-width: 18px;
      height: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #ff4000;
      border-radius: 4px;
      margin-right: 6px;
      line-height: 18px;
      font-weight: 700;
      color: #fff;
      font-size: 10px;
      vertical-align: top;
      position: absolute;
      left: 0;
      top: 0;
    }

    .goods-type-icon {
      width: 18px;
      height: 18px;
      min-width: 18px;
      object-fit: contain;
      vertical-align: top;
      border-radius: 4px;
      background: #fff;
      position: absolute;
      left: 0;
      top: 0;
    }

    .goods-title {
      display: -webkit-box;
      margin: 0;
      line-height: 18px;
      color: #232323;
      overflow: hidden;
      font-size: 12px;
      font-weight: 400;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      position: relative;
      padding-left: 24px;
      min-height: 36px;
    }

    .goods-title-text {
      word-break: break-word;
    }
  }

  .goods-price-wrap {
    margin-top: 8px;
    display: flex;
    align-items: baseline;
    gap: 4px;
    min-height: 24px;
  }

  .goods-price {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
    color: #ff4600;
  }

  .goods-origin-price {
    font-size: 12px;
    line-height: 16px;
    color: #999;
    text-decoration: line-through;
  }

  .goods-sales-row {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 12px;
    line-height: 18px;
    color: #1a1817;
  }

  .goods-sales-label,
  .goods-sales-value {
    font-size: 12px;
    line-height: 18px;
    color: #1a1817;
  }
}
</style>
